import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { resolveCleaningReleasedPropertyIssue } from "@/lib/cleaningIssueApi";
import {
  canCleaningManagementSelfResolve,
  issueStatusBadgeClass,
  issueStatusLabelPl,
  type CleaningIssueHistoryRow,
} from "@/lib/issueStatusLabels";

type CleaningIssueHistoryTableProps = {
  issues: CleaningIssueHistoryRow[];
  canEdit: boolean;
  onResolved: () => void | Promise<void>;
};

export function CleaningIssueHistoryTable({
  issues,
  canEdit,
  onResolved,
}: CleaningIssueHistoryTableProps) {
  const [issueToResolve, setIssueToResolve] = useState<CleaningIssueHistoryRow | null>(null);
  const [isResolving, setIsResolving] = useState(false);

  const handleConfirmResolve = async () => {
    if (!issueToResolve) return;
    try {
      setIsResolving(true);
      await resolveCleaningReleasedPropertyIssue(issueToResolve.id);
      toast.success("Usterka oznaczona jako rozwiązana");
      setIssueToResolve(null);
      await onResolved();
    } catch (error) {
      console.error("[CleaningIssueHistoryTable] resolve:", error);
      toast.error(error instanceof Error ? error.message : "Nie udało się zmienić statusu");
    } finally {
      setIsResolving(false);
    }
  };

  return (
    <>
      <div className="border border-border/60 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-200 dark:bg-slate-800">
              <TableHead>Data zgłoszenia</TableHead>
              <TableHead>Opis usterki</TableHead>
              <TableHead>Zgłaszający</TableHead>
              <TableHead>Status</TableHead>
              {canEdit ? <TableHead className="text-right">Akcje</TableHead> : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {issues.map((issue) => {
              const reporterName = issue.reporter?.full_name ?? "Nieznany";
              const canResolve = canEdit && canCleaningManagementSelfResolve(issue);
              return (
                <TableRow key={issue.id}>
                  <TableCell>
                    {issue.created_at
                      ? new Date(issue.created_at).toLocaleString("pl-PL", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "Brak daty"}
                  </TableCell>
                  <TableCell className="max-w-[220px]">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            className={cn(
                              "truncate cursor-default",
                              !issue.description ? "text-muted-foreground" : "",
                            )}
                            title={issue.description || ""}
                          >
                            {issue.description || "Brak opisu"}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-sm whitespace-pre-wrap break-words">
                          {issue.description || "Brak opisu"}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                  <TableCell>{reporterName}</TableCell>
                  <TableCell>
                    <Badge
                      variant={issue.status === "resolved" ? "default" : "outline"}
                      className={issueStatusBadgeClass(issue.status)}
                    >
                      {issueStatusLabelPl(issue.status)}
                    </Badge>
                  </TableCell>
                  {canEdit ? (
                    <TableCell className="text-right">
                      {canResolve ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setIssueToResolve(issue)}
                        >
                          Oznacz jako rozwiązane
                        </Button>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  ) : null}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <AlertDialog
        open={issueToResolve !== null}
        onOpenChange={(open) => {
          if (!open && !isResolving) setIssueToResolve(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Oznaczyć usterkę jako rozwiązaną?</AlertDialogTitle>
            <AlertDialogDescription>
              Zrób to tylko jeśli naprawiliście zgłoszenie samodzielnie, a administrator jeszcze nie
              przekazał go do serwisu ani na giełdę.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isResolving}>Anuluj</AlertDialogCancel>
            <AlertDialogAction
              disabled={isResolving}
              onClick={(event) => {
                event.preventDefault();
                void handleConfirmResolve();
              }}
            >
              {isResolving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Oznacz jako rozwiązane
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
